package com.example.mystybag.login.views

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.PressInteraction
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.coroutines.flow.collect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.common.extensions.AsyncImage
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.defines.CommunicationDefines.Companion.DOMAIN
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.rememberIsWebPortraitLayout
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme
import com.example.mystybag.core.ui.theme.LocalTheme.p1
import com.example.mystybag.core.ui.theme.LocalTheme.p10
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p22
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p33
import com.example.mystybag.core.ui.theme.LocalTheme.p56
import com.example.mystybag.core.ui.theme.LocalTheme.p64
import com.example.mystybag.core.ui.theme.LocalTheme.p72
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p9
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.login.helpers.navigateToLoginHelp
import com.example.mystybag.login.views.components.WebInlineLanguageSwitcher
import com.example.mystybag.login.logic.VerifyEmailAddressViewModel
import com.example.mystybag.login.model.KeyButtonType
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource

private val WELCOME_LOGO_MARK: String
    get() = "https://$DOMAIN/static/email_photos/logo_40.png"
private val keypadHorizontalSpacing = 6.dp
private val keypadVerticalSpacing = 6.dp
private val keypadButtonSizeMin = 44.dp
private val keypadButtonSizeMaxWidth = 127.dp
private val keypadButtonSizeMaxHeight = 72.dp

@Composable
fun KeypadButton(
    key: KeyButtonType,
    surfaceContainerLowest: androidx.compose.ui.graphics.Color,
    onKeyPress: (KeyButtonType) -> Unit,
    modifier: Modifier = Modifier
) {
    val shape = RoundedCornerShape(p32)
    val interactionSource = remember { MutableInteractionSource() }
    var isPressed by remember { mutableStateOf(false) }
    LaunchedEffect(interactionSource) {
        interactionSource.interactions.collect { interaction ->
            when (interaction) {
                is PressInteraction.Press -> isPressed = true
                is PressInteraction.Release, is PressInteraction.Cancel -> isPressed = false
            }
        }
    }

    Box(
        modifier = modifier
            .clip(shape)
            .background(surfaceContainerLowest)
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                onClick = { onKeyPress(key) }
            )
    ) {
        Box(
            contentAlignment = Alignment.Center,
            modifier = Modifier
                .fillMaxSize()
                .padding(p10)
        ) {
            when (key) {
                KeyButtonType.TYPE_BACK -> Image(
                    modifier = Modifier.size(p33, 23.dp),
                    painter = painterResource(SRC.images.login_backspace_icon),
                    contentDescription = null,
                    colorFilter = ColorFilter.tint(AppTheme.current.colors.onSurface)
                )
                else -> Texts.HeadlineLarge(
                    text = key.value,
                    fontWeight = FontWeight.Medium,
                    color = AppTheme.current.colors.onSurface
                )
            }
        }
        // Собствен ripple overlay, отрязан от същата форма – покрива целия бутон включително ъглите
        if (isPressed) {
            Box(
                modifier = Modifier
                    .matchParentSize()
                    .clip(shape)
                    .background(AppTheme.current.colors.onSurface.copy(alpha = 0.12f))
            )
        }
    }
}
@Composable
fun VerifyEmailAddressView(viewModel: VerifyEmailAddressViewModel) {
    if (PlatformHelper.isWeb && !rememberIsWebPortraitLayout()) {
        VerifyEmailAddressViewWeb(viewModel)
        return
    }

    VerifyEmailAddressViewMobile(viewModel)
}

@Composable
private fun VerifyEmailAddressViewWeb(viewModel: VerifyEmailAddressViewModel) {
    val state = viewModel.uiState.subscribeAsState()
    val theme = AppTheme.current
    val focusRequester = remember { FocusRequester() }

    LaunchedEffect(Unit) {
        focusRequester.requestFocus()
    }

    Column(
        modifier = Modifier.fillMaxSize(),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(106.dp)
                .padding(horizontal = 40.dp, vertical = 32.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(20.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier
                        .size(48.dp)
                        .clip(CircleShape)
                        .background(theme.colors.surfaceContainerLowest)
                        .noRippleClickable { viewModel.onBackButtonClicked() },
                    contentAlignment = Alignment.Center
                ) {
                    Image(
                        painter = painterResource(SRC.images.arrow_back),
                        contentDescription = null,
                        modifier = Modifier.size(18.dp)
                    )
                }
                Row(
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    AsyncImage(
                        link = WELCOME_LOGO_MARK,
                        placeholder = null,
                        modifier = Modifier
                            .width(40.dp)
                            .height(40.dp)
                            .clip(RoundedCornerShape(10.dp)),
                        contentScale = ContentScale.Fit
                    )
                    androidx.compose.material3.Text(
                        text = "MystyBag",
                        color = theme.colors.onSurface,
                        fontFamily = LocalTheme.googleSans,
                        fontSize = 24.sp,
                        lineHeight = 40.sp,
                        fontWeight = FontWeight.Bold
                    )
                }
            }
            Row(
                horizontalArrangement = Arrangement.spacedBy(24.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Texts.BodyMedium(
                    text = "Help",
                    color = theme.colors.onSurfaceVariant,
                    modifier = Modifier.noRippleClickable { viewModel.navigateToLoginHelp() }
                )
                WebInlineLanguageSwitcher()
            }
        }

        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(top = 120.dp),
            contentAlignment = Alignment.TopCenter
        ) {
            Column(
                modifier = Modifier.width(530.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Image(
                    painter = painterResource(SRC.images.confirm_email_bell_icon),
                    contentDescription = null,
                    modifier = Modifier.size(64.dp),
                    contentScale = ContentScale.Fit
                )
                Spacer(modifier = Modifier.height(48.dp))
                Texts.DisplaySmall(
                    text = stringResource(SR.strings.confirm_email_address_title),
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth()
                )
                Spacer(modifier = Modifier.height(24.dp))
                Texts.BodyMedium(
                    text = stringResource(SR.strings.confirm_email_code_sent, viewModel.email),
                    textAlign = TextAlign.Center,
                    color = theme.colors.onSurfaceVariant,
                    modifier = Modifier.fillMaxWidth()
                )
                Spacer(modifier = Modifier.height(48.dp))
                BasicTextField(
                    value = state.value.layoutInput,
                    onValueChange = viewModel::updateVerificationCode,
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    modifier = Modifier
                        .size(1.dp)
                        .focusRequester(focusRequester)
                )
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { focusRequester.requestFocus() },
                    horizontalArrangement = Arrangement.spacedBy(16.dp, Alignment.CenterHorizontally)
                ) {
                    repeat(6) { index ->
                        val hasDigit = index < state.value.layoutInput.length
                        Box(
                            modifier = Modifier
                                .size(56.dp)
                                .border(
                                    p1,
                                    if (hasDigit) theme.colors.primary else theme.colors.outlineVariant,
                                    RoundedCornerShape(8.dp)
                                )
                                .clip(RoundedCornerShape(8.dp))
                                .background(theme.colors.surface),
                            contentAlignment = Alignment.Center
                        ) {
                            Texts.HeadlineMedium(
                                text = state.value.layoutInput.getOrNull(index)?.toString().orEmpty(),
                                color = theme.colors.onSurface
                            )
                        }
                    }
                }
                Spacer(modifier = Modifier.height(48.dp))
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(56.dp)
                        .clip(CircleShape)
                        .background(theme.colors.primary)
                        .noRippleClickable {
                            if (state.value.layoutInput.length == 6) {
                                viewModel.updateVerificationCode(state.value.layoutInput)
                            }
                        },
                    contentAlignment = Alignment.Center
                ) {
                    Texts.BodyMedium(
                        text = stringResource(SR.strings.next),
                        color = theme.colors.onPrimary
                    )
                }
            }
        }
    }
}

@Composable
private fun VerifyEmailAddressViewMobile(viewModel: VerifyEmailAddressViewModel) {
    val state = viewModel.uiState.subscribeAsState()
    val surfaceContainerLowest = AppTheme.current.colors.surfaceContainerLowest
    val outlineVariant = AppTheme.current.colors.outlineVariant

    UIView(
        viewModel = viewModel,
        horizontalAlignment = Alignment.Start
    ) {
        BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
            val heightPx = with(LocalDensity.current) { maxHeight.toPx() }
            val showHeader = heightPx >= 1800

            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .fillMaxWidth()
                    .padding(horizontal = p16),
                horizontalAlignment = Alignment.Start,
                verticalArrangement = Arrangement.spacedBy(0.dp)
            ) {
                if (showHeader) {
                    Spacer(modifier = Modifier.height(p12))

                    Box(
                        modifier = Modifier.size(p64),
                        contentAlignment = Alignment.Center
                    ) {
                        Image(
                            painter = painterResource(SRC.images.confirm_email_bell_icon),
                            contentDescription = null,
                            modifier = Modifier.fillMaxWidth(),
                            contentScale = ContentScale.Fit
                        )
                    }

                    Spacer(modifier = Modifier.height(p32))
                    Column(
                        verticalArrangement = Arrangement.spacedBy(p24),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Texts.HeadlineLarge(
                            text = stringResource(SR.strings.confirm_email_address_title),
                            modifier = Modifier.fillMaxWidth()
                        )
                        Texts.BodyMedium(
                            text = stringResource(SR.strings.confirm_email_code_sent, viewModel.email),
                            modifier = Modifier.fillMaxWidth()
                        )
                    }

                    Spacer(modifier = Modifier.height(p32))
                }
                else
                    Texts.BodyMedium(
                        text = stringResource(SR.strings.confirm_email_code_sent, viewModel.email),
                        modifier = Modifier.fillMaxWidth()
                    )

                Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(6.dp)
            ) {
                repeat(6) { index ->
                    val hasDigit = index < state.value.layoutInput.length
                    val borderColor =
                        if (hasDigit) AppTheme.current.colors.primary else outlineVariant
                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .height(p56)
                            .border(p1, borderColor, RoundedCornerShape(p12))
                            .clip(RoundedCornerShape(p12))
                            .background(AppTheme.current.colors.surface),
                        contentAlignment = Alignment.Center
                    ) {
                        val digit = state.value.layoutInput.getOrNull(index)?.toString() ?: ""
                        Texts.HeadlineMedium(text = digit)
                    }
                }
            }
            BoxWithConstraints(modifier = Modifier.weight(1f)) {
                val bottomPadding = 8.dp
                val keypadTopGap = 24.dp
                val keypadAreaHeight = maxHeight - keypadTopGap - bottomPadding
                val keypadAreaWidth = maxWidth
                val buttonHeight: Dp = maxOf(
                    keypadButtonSizeMin,
                    minOf((keypadAreaHeight - 18.dp) / 4, keypadButtonSizeMaxHeight)
                )
                val buttonWidth: Dp = maxOf(
                    keypadButtonSizeMin,
                    minOf((keypadAreaWidth - 12.dp) / 3, keypadButtonSizeMaxWidth)
                )

                Column(modifier = Modifier.fillMaxSize()) {
                    Box(modifier = Modifier.weight(1f))

                    // Keypad at bottom with safe area padding so it’s not cut off
                    Column(
                        verticalArrangement = Arrangement.spacedBy(keypadVerticalSpacing),
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(top = keypadTopGap, bottom = bottomPadding),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        // Row 1: 1, 2, 3
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(
                                keypadHorizontalSpacing,
                                Alignment.CenterHorizontally
                            )
                        ) {
                            listOf(
                                KeyButtonType.TYPE_1,
                                KeyButtonType.TYPE_2,
                                KeyButtonType.TYPE_3
                            ).forEach { key ->
                                Box(
                                    modifier = Modifier.weight(1f),
                                    contentAlignment = Alignment.Center
                                ) {
                                    KeypadButton(
                                        key = key,
                                        surfaceContainerLowest = surfaceContainerLowest,
                                        onKeyPress = viewModel::onKeyboardClick,
                                        modifier = Modifier.size(buttonWidth, buttonHeight)
                                    )
                                }
                            }
                        }
                        // Row 2: 4, 5, 6
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(
                                keypadHorizontalSpacing,
                                Alignment.CenterHorizontally
                            )
                        ) {
                            listOf(
                                KeyButtonType.TYPE_4,
                                KeyButtonType.TYPE_5,
                                KeyButtonType.TYPE_6
                            ).forEach { key ->
                                Box(
                                    modifier = Modifier.weight(1f),
                                    contentAlignment = Alignment.Center
                                ) {
                                    KeypadButton(
                                        key = key,
                                        surfaceContainerLowest = surfaceContainerLowest,
                                        onKeyPress = viewModel::onKeyboardClick,
                                        modifier = Modifier.size(buttonWidth, buttonHeight)
                                    )
                                }
                            }
                        }
                        // Row 3: 7, 8, 9
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(
                                keypadHorizontalSpacing,
                                Alignment.CenterHorizontally
                            )
                        ) {
                            listOf(
                                KeyButtonType.TYPE_7,
                                KeyButtonType.TYPE_8,
                                KeyButtonType.TYPE_9
                            ).forEach { key ->
                                Box(
                                    modifier = Modifier.weight(1f),
                                    contentAlignment = Alignment.Center
                                ) {
                                    KeypadButton(
                                        key = key,
                                        surfaceContainerLowest = surfaceContainerLowest,
                                        onKeyPress = viewModel::onKeyboardClick,
                                        modifier = Modifier.size(buttonWidth, buttonHeight)
                                    )
                                }
                            }
                        }
                        // Row 4: empty, 0, backspace – three equal columns
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(
                                keypadHorizontalSpacing,
                                Alignment.CenterHorizontally
                            )
                        ) {
                            Box(modifier = Modifier.weight(1f).height(buttonHeight)) // empty cell
                            Box(
                                modifier = Modifier.weight(1f),
                                contentAlignment = Alignment.Center
                            ) {
                                KeypadButton(
                                    key = KeyButtonType.TYPE_0,
                                    surfaceContainerLowest = surfaceContainerLowest,
                                    onKeyPress = viewModel::onKeyboardClick,
                                    modifier = Modifier.size(buttonWidth, buttonHeight)
                                )
                            }
                            Box(
                                modifier = Modifier.weight(1f),
                                contentAlignment = Alignment.Center
                            ) {
                                KeypadButton(
                                    key = KeyButtonType.TYPE_BACK,
                                    surfaceContainerLowest = surfaceContainerLowest,
                                    onKeyPress = viewModel::onKeyboardClick,
                                    modifier = Modifier.size(buttonWidth, buttonHeight)
                                )
                            }
                        }
                    }
                }
            }
        }
        }
    }

}
